/**
 * Tiered Rewards Bar
 * Handles the progress bar and dynamic messaging for cart rewards
 */

(function() {
  'use strict';

  const selectors = {
    tieredRewardsBar: '[data-tiered-rewards-bar]',
    rewardsMessage: '[data-rewards-message]',
    rewardsIcon: '[data-rewards-icon]',
    rewardsProgress: '[data-rewards-progress]',
    tier: '[data-tier]',
    tierLabel: '[data-tier-label]',
    rewardMessages: '[data-reward-messages]',
    shippingMessage: '[data-shipping-message]',
    giftsMessage: '[data-gifts-message]'
  };

  const classes = {
    loaded: 'tiered-rewards-bar--loaded',
    success: 'tiered-rewards-bar--success',
    tierReached: 'tiered-rewards-bar__tier--reached',
    tierActive: 'tiered-rewards-bar__tier--active',
    labelAchieved: 'tiered-rewards-bar__label--achieved',
    labelAllReached: 'tiered-rewards-bar__label--all-reached',
    hidden: 'hidden',
    noTransition: 'tiered-rewards-bar--no-transition'
  };

  // Store the last known visual state to restore after DOM replacement
  let lastState = null;

  // Track previously reached tiers for achievement feedback
  let previouslyReachedTiers = [];
  let previouslyReachedInitialized = false;
  let achievementMessageTimeout = null;
  let achievementActive = false;
  let achievementIconHTML = '';
  let achievementMessageHTML = '';

  function captureState(node) {
    if (!node) return null;
    const progressBar = node.querySelector(selectors.rewardsProgress);
    const messageEl = node.querySelector(selectors.rewardsMessage);
    const iconEl = node.querySelector(selectors.rewardsIcon);

    // Capture tier marker inline styles (positions set by JS)
    var tierStyles = {};
    node.querySelectorAll(selectors.tier).forEach(tierEl => {
      tierStyles[tierEl.dataset.tier] = tierEl.getAttribute('style') || '';
    });

    return {
      progressWidth: progressBar ? progressBar.style.getPropertyValue('--progress-width') : '0%',
      messageHTML: messageEl ? messageEl.innerHTML : '',
      iconHTML: iconEl ? iconEl.innerHTML : '',
      isSuccess: node.classList.contains(classes.success),
      reachedTiers: Array.from(node.querySelectorAll('.' + classes.tierReached)).map(el => el.dataset.tier),
      achievedLabels: Array.from(node.querySelectorAll('.' + classes.labelAchieved)).map(el => el.dataset.tierLabel),
      allReachedLabels: Array.from(node.querySelectorAll('.' + classes.labelAllReached)).map(el => el.dataset.tierLabel),
      tierStyles: tierStyles
    };
  }

  function restoreState(node, state) {
    if (!node || !state) return;

    // Disable transitions
    node.classList.add(classes.noTransition);

    // Restore progress width
    const progressBar = node.querySelector(selectors.rewardsProgress);
    if (progressBar) {
      progressBar.style.setProperty('--progress-width', state.progressWidth);
    }

    // Restore message and icon to prevent blank flash
    // If achievement is active, use the stored achievement content instead of captured state
    const messageEl = node.querySelector(selectors.rewardsMessage);
    if (messageEl) {
      messageEl.innerHTML = (achievementActive && achievementMessageHTML) ? achievementMessageHTML : state.messageHTML;
    }
    const iconEl = node.querySelector(selectors.rewardsIcon);
    if (iconEl) {
      iconEl.innerHTML = (achievementActive && achievementIconHTML) ? achievementIconHTML : state.iconHTML;
    }

    // Restore tier marker positions and reached states
    if (state.tierStyles) {
      Object.keys(state.tierStyles).forEach(tierType => {
        const tierEl = node.querySelector('[data-tier="' + tierType + '"]');
        if (tierEl && state.tierStyles[tierType]) {
          tierEl.setAttribute('style', state.tierStyles[tierType]);
        }
      });
    }
    state.reachedTiers.forEach(tierType => {
      const tierEl = node.querySelector('[data-tier="' + tierType + '"]');
      if (tierEl) tierEl.classList.add(classes.tierReached);
    });

    // Restore label states
    state.achievedLabels.forEach(tierType => {
      const labelEl = node.querySelector('[data-tier-label="' + tierType + '"]');
      if (labelEl) labelEl.classList.add(classes.labelAchieved);
    });
    state.allReachedLabels.forEach(tierType => {
      const labelEl = node.querySelector('[data-tier-label="' + tierType + '"]');
      if (labelEl) labelEl.classList.add(classes.labelAllReached);
    });

    // Restore success class
    if (state.isSuccess) {
      node.classList.add(classes.success);
    }

    // Mark as loaded immediately so message is visible
    node.classList.add(classes.loaded);
  }

  function formatMoney(cents) {
    if (typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
      return formatWithTheme(cents, window.theme.moneyFormat);
    }
    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    return '€' + amount.replace('.', ',');
  }

  function formatWithTheme(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision === undefined ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents_part = parts[1] ? decimal + parts[1] : '';

      return dollars + cents_part;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getCurrencyRate() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  }

  function parseThreshold(value) {
    if (!value || value === '' || value === '0') return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : Math.round(parsed * getCurrencyRate() * 100);
  }

  function initTieredRewardsBar(node) {
    if (!node) return;

    const dataset = node.dataset;
    const cartTotal = parseInt(dataset.cartTotal, 10) || 0;

    const shippingThreshold = parseThreshold(dataset.shippingThreshold);
    const giftboxThreshold = parseThreshold(dataset.giftboxThreshold);
    const earringThreshold = parseThreshold(dataset.earringThreshold);

    // Build thresholds array with custom labels from data attributes
    const shippingLabel = dataset.shippingLabel || 'Free shipping';
    const giftboxLabel = dataset.giftboxLabel || 'Free giftbox';
    const earringLabel = dataset.earringLabel || 'Free earring';
    const thresholds = [];
    if (shippingThreshold) thresholds.push({ type: 'shipping', value: shippingThreshold, label: shippingLabel });
    if (giftboxThreshold) thresholds.push({ type: 'giftbox', value: giftboxThreshold, label: giftboxLabel });
    if (earringThreshold) thresholds.push({ type: 'earring', value: earringThreshold, label: earringLabel });

    // Sort by threshold value
    thresholds.sort((a, b) => a.value - b.value);

    if (thresholds.length === 0) return;

    const maxThreshold = thresholds[thresholds.length - 1].value;

    // Update progress bar
    updateProgressBar(node, cartTotal, thresholds);

    // Update message (skip if achievement message is currently showing)
    if (!achievementActive) {
      updateMessage(node, cartTotal, thresholds);
    }

    // Update tier markers
    updateTierMarkers(node, cartTotal, thresholds);

    // Mark as loaded
    node.classList.add(classes.loaded);

    // Initialize previously reached tiers only on first load (so we don't show achievement on page load)
    // On subsequent calls (from MutationObserver re-init), keep the existing previouslyReachedTiers
    // so that updateTieredRewardsBarValues can detect newly achieved tiers
    if (!previouslyReachedInitialized) {
      previouslyReachedTiers = thresholds.filter(t => cartTotal >= t.value).map(t => t.type);
      previouslyReachedInitialized = true;
    }

    // Check for all tiers complete
    if (cartTotal >= maxThreshold) {
      node.classList.add(classes.success);
    }
  }

  function updateProgressBar(node, cartTotal, thresholds) {
    const progressBar = node.querySelector(selectors.rewardsProgress);
    if (!progressBar) return;

    const tierCount = thresholds.length;
    const maxThreshold = thresholds[tierCount - 1].value;

    // Calculate progress based on evenly spaced tiers
    // Each tier represents 1/tierCount of the bar (e.g., 33.33% for 3 tiers)
    let progress = 0;

    for (let i = 0; i < tierCount; i++) {
      const tierThreshold = thresholds[i].value;
      const tierWidth = 100 / tierCount;
      const tierStart = i * tierWidth;

      if (cartTotal >= tierThreshold) {
        // This tier is fully complete
        progress = tierStart + tierWidth;
      } else if (i === 0) {
        // First tier, progress from 0 to first threshold
        progress = (cartTotal / tierThreshold) * tierWidth;
        break;
      } else {
        // Partially through this tier
        const prevThreshold = thresholds[i - 1].value;
        const tierProgress = (cartTotal - prevThreshold) / (tierThreshold - prevThreshold);
        progress = tierStart + (tierProgress * tierWidth);
        break;
      }
    }

    const progressWidth = Math.min(progress, 100) + '%';
    progressBar.style.setProperty('--progress-width', progressWidth);
  }

  function updateMessage(node, cartTotal, thresholds) {
    const messageEl = node.querySelector(selectors.rewardsMessage);
    const iconEl = node.querySelector(selectors.rewardsIcon);
    if (!messageEl) return;

    // Find the next unreached tier
    let nextTier = null;
    for (const tier of thresholds) {
      if (cartTotal < tier.value) {
        nextTier = tier;
        break;
      }
    }

    if (nextTier) {
      const remaining = nextTier.value - cartTotal;
      const remainingFormatted = formatMoney(remaining);

      // Update icon based on next tier
      if (iconEl) {
        updateIcon(iconEl, nextTier.type);
      }

      const labelLower = nextTier.label.charAt(0).toLowerCase() + nextTier.label.slice(1);
      messageEl.innerHTML = 'Only <strong>' + remainingFormatted + '</strong> away from ' + labelLower + '!';
    } else {
      // All tiers reached - show confetti icon
      if (iconEl) {
        updateIcon(iconEl, 'confetti');
      }
      messageEl.innerHTML = 'Congrats! <strong style="color: var(--color-text-success);">All gifts are in your cart!</strong>';
    }
  }

  function updateIcon(iconEl, tierType) {
    // Icon SVGs for message area (18px)
    const icons = {
      shipping: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M1 12.5V6h13v12H5.5M14 18h2.5M22 18h1v-5.5L20 9h-6v9" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="7" cy="18" r="2.5" stroke="currentColor" stroke-width="1.2"/><circle cx="18.5" cy="18" r="2.5" stroke="currentColor" stroke-width="1.2"/></svg>',
      giftbox: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M3 8h18v13H3V8z" stroke="currentColor" stroke-width="1.2"/><path d="M1 5h22v3H1V5z" stroke="currentColor" stroke-width="1.2"/><path d="M12 8v13M12 5v3" stroke="currentColor" stroke-width="1.2"/><path d="M12 5c0-2-1.5-3-3-3s-3 1.5-3 3h6z" stroke="currentColor" stroke-width="1.2"/><path d="M12 5c0-2 1.5-3 3-3s3 1.5 3 3h-6z" stroke="currentColor" stroke-width="1.2"/></svg>',
      earring: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="12" cy="4" r="2.5" stroke="currentColor" stroke-width="1.2"/><path d="M12 6.5v2" stroke="currentColor" stroke-width="1.2"/><circle cx="12" cy="14" r="5.5" stroke="currentColor" stroke-width="1.2"/></svg>',
      confetti: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M4.5 22L2 12l10 2.5L4.5 22z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M12 14.5L9.5 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M14.5 12L20 9.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/><circle cx="17" cy="5" r="1.5" fill="currentColor"/><circle cx="20" cy="14" r="1.5" fill="currentColor"/><circle cx="15" cy="17" r="1" fill="currentColor"/><circle cx="19" cy="19" r="1" fill="currentColor"/><path d="M5 2l1 2M22 7l-2 1M17 21l-1-2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>',
      truck: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M1 12.5V6h13v12H5.5M14 18h2.5M22 18h1v-5.5L20 9h-6v9" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="7" cy="18" r="2.5" stroke="currentColor" stroke-width="1.2"/><circle cx="18.5" cy="18" r="2.5" stroke="currentColor" stroke-width="1.2"/></svg>',
      gift: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M3 8h18v13H3V8z" stroke="currentColor" stroke-width="1.2"/><path d="M1 5h22v3H1V5z" stroke="currentColor" stroke-width="1.2"/><path d="M12 8v13M12 5v3" stroke="currentColor" stroke-width="1.2"/><path d="M12 5c0-2-1.5-3-3-3s-3 1.5-3 3h6z" stroke="currentColor" stroke-width="1.2"/><path d="M12 5c0-2 1.5-3 3-3s3 1.5 3 3h-6z" stroke="currentColor" stroke-width="1.2"/></svg>',
      star: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>',
      heart: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" stroke-width="1.2"/></svg>',
      diamond: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M6 3h12l4 7-10 12L2 10l4-7z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M2 10h20" stroke="currentColor" stroke-width="1.2"/><path d="M12 22V10" stroke="currentColor" stroke-width="1.2"/></svg>',
      ring: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="12" cy="14" r="7" stroke="currentColor" stroke-width="1.2"/><path d="M9 3h6l1.5 4H7.5L9 3z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M12 7v2" stroke="currentColor" stroke-width="1.2"/></svg>',
      flower: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.2"/><circle cx="12" cy="6" r="2.5" stroke="currentColor" stroke-width="1.2"/><circle cx="17.5" cy="9.5" r="2.5" stroke="currentColor" stroke-width="1.2"/><circle cx="15.5" cy="16" r="2.5" stroke="currentColor" stroke-width="1.2"/><circle cx="8.5" cy="16" r="2.5" stroke="currentColor" stroke-width="1.2"/><circle cx="6.5" cy="9.5" r="2.5" stroke="currentColor" stroke-width="1.2"/></svg>',
      bag: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M4 7h16l-1.5 14h-13L4 7z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M8.5 7V5.5a3.5 3.5 0 017 0V7" stroke="currentColor" stroke-width="1.2"/></svg>',
      crown: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path d="M3 18V8l4.5 4L12 5l4.5 7L21 8v10H3z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M3 18h18" stroke="currentColor" stroke-width="1.2"/></svg>'
    };

    iconEl.innerHTML = icons[tierType] || icons.truck;
  }

  function showAchievementMessage(node, achievedTier, cartTotal, thresholds) {
    const messageEl = node.querySelector(selectors.rewardsMessage);
    const iconEl = node.querySelector(selectors.rewardsIcon);
    if (!messageEl) return;

    // Read duration from data attribute (in seconds), 0 = disabled
    const duration = parseInt(node.dataset.achievementDuration, 10);
    if (isNaN(duration) || duration <= 0) return;

    // Clear any existing achievement timeout
    if (achievementMessageTimeout) {
      clearTimeout(achievementMessageTimeout);
    }

    achievementActive = true;

    // Show the achieved tier's icon during the achievement message
    if (iconEl) {
      updateIcon(iconEl, achievedTier.type);
      achievementIconHTML = iconEl.innerHTML;
    }

    // Show "You unlocked [label]!" message - only bold the label
    achievementMessageHTML = 'You unlocked <strong>' + achievedTier.label + '</strong>!';
    messageEl.innerHTML = achievementMessageHTML;

    // After configured duration, revert to normal message
    // Use querySelector to find the current bar in the DOM (node ref may become stale after DOM replacement)
    achievementMessageTimeout = setTimeout(function() {
      achievementActive = false;
      achievementIconHTML = '';
      achievementMessageHTML = '';
      var currentBar = document.querySelector(selectors.tieredRewardsBar + '.' + classes.loaded);
      if (currentBar) {
        var dataset = currentBar.dataset;
        var total = parseInt(dataset.cartTotal, 10) || 0;
        var st = parseThreshold(dataset.shippingThreshold);
        var gt = parseThreshold(dataset.giftboxThreshold);
        var et = parseThreshold(dataset.earringThreshold);
        var sl = dataset.shippingLabel || 'Free shipping';
        var gl = dataset.giftboxLabel || 'Free giftbox';
        var el2 = dataset.earringLabel || 'Free earring';
        var th = [];
        if (st) th.push({ type: 'shipping', value: st, label: sl });
        if (gt) th.push({ type: 'giftbox', value: gt, label: gl });
        if (et) th.push({ type: 'earring', value: et, label: el2 });
        th.sort(function(a, b) { return a.value - b.value; });
        updateMessage(currentBar, total, th);
      }
      achievementMessageTimeout = null;
    }, duration * 1000);
  }

  function updateTierMarkers(node, cartTotal, thresholds) {
    const tiers = node.querySelectorAll(selectors.tier);
    const labels = node.querySelectorAll(selectors.tierLabel);
    const tierCount = thresholds.length;

    // Position tier markers evenly spaced (1/3, 2/3, 3/3 for 3 tiers)
    tiers.forEach((tierEl, index) => {
      const tierType = tierEl.dataset.tier;
      const threshold = thresholds.find(t => t.type === tierType);
      const tierIndex = thresholds.findIndex(t => t.type === tierType);

      if (threshold) {
        // Calculate position as evenly spaced (e.g., 33.33%, 66.66%, 100% for 3 tiers)
        const positionPercent = ((tierIndex + 1) / tierCount) * 100;
        tierEl.style.position = 'absolute';
        // Center vertically: top 50% minus half the icon height (12px)
        tierEl.style.top = '50%';
        tierEl.style.marginTop = '-12px';
        tierEl.style.marginLeft = '0';
        tierEl.style.marginRight = '0';
        tierEl.style.marginBottom = '0';

        // For the last tier (100%), position at the right edge with icon centered on end of track
        if (tierIndex === tierCount - 1) {
          tierEl.style.left = 'auto';
          tierEl.style.right = '0';
          tierEl.style.transform = 'translateX(0)';
        } else {
          tierEl.style.left = positionPercent + '%';
          tierEl.style.right = 'auto';
          tierEl.style.transform = 'translateX(-50%)';
        }

        if (cartTotal >= threshold.value) {
          tierEl.classList.add(classes.tierReached);
        } else {
          tierEl.classList.remove(classes.tierReached);
        }
      }
    });

    // Check if all tiers are reached
    const maxThreshold = thresholds[thresholds.length - 1].value;
    const allReached = cartTotal >= maxThreshold;

    // Update label states (positioning handled by CSS flexbox)
    labels.forEach((labelEl) => {
      const tierType = labelEl.dataset.tierLabel;
      const threshold = thresholds.find(t => t.type === tierType);

      if (threshold) {
        // Remove both classes first
        labelEl.classList.remove(classes.labelAchieved, classes.labelAllReached);

        if (cartTotal >= threshold.value) {
          if (allReached) {
            // All tiers reached - use green color
            labelEl.classList.add(classes.labelAllReached);
          } else {
            // This tier reached but not all - use black color
            labelEl.classList.add(classes.labelAchieved);
          }
        }
      }
    });
  }

  function initRewardMessages(node) {
    if (!node) return;

    const dataset = node.dataset;
    const cartTotal = parseInt(dataset.cartTotal, 10) || 0;

    const shippingThreshold = parseThreshold(dataset.shippingThreshold);
    const giftboxThreshold = parseThreshold(dataset.giftboxThreshold);
    const earringThreshold = parseThreshold(dataset.earringThreshold);

    const shippingMessageEl = node.querySelector(selectors.shippingMessage);
    const giftsMessageEl = node.querySelector(selectors.giftsMessage);

    // Shipping message
    if (shippingMessageEl) {
      // Select the text span (not the icon span which has class "icon")
      const textSpan = shippingMessageEl.querySelector('span:not(.icon)');
      if (textSpan) {
        if (shippingThreshold && cartTotal >= shippingThreshold) {
          textSpan.textContent = "You've unlocked free shipping!";
        } else {
          textSpan.textContent = 'Shipping calculated at checkout.';
        }
      }
    }

    // Gifts message (giftbox and/or earring)
    if (giftsMessageEl) {
      const qualifiedGifts = [];

      if (giftboxThreshold && cartTotal >= giftboxThreshold) {
        qualifiedGifts.push('giftbox');
      }
      if (earringThreshold && cartTotal >= earringThreshold) {
        qualifiedGifts.push('earring');
      }

      if (qualifiedGifts.length > 0) {
        const giftsText = qualifiedGifts.join(' & ');
        giftsMessageEl.innerHTML = '<span class="icon"><svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M3 8h18v13H3V8z" stroke="currentColor" stroke-width="1.2"/><path d="M1 5h22v3H1V5z" stroke="currentColor" stroke-width="1.2"/><path d="M12 8v13M12 5v3" stroke="currentColor" stroke-width="1.2"/><path d="M12 5c0-2-1.5-3-3-3s-3 1.5-3 3h6z" stroke="currentColor" stroke-width="1.2"/><path d="M12 5c0-2 1.5-3 3-3s3 1.5 3 3h-6z" stroke="currentColor" stroke-width="1.2"/></svg></span><span>You\'ve unlocked a free ' + giftsText + '!</span>';
        giftsMessageEl.classList.remove(classes.hidden);
      } else {
        giftsMessageEl.innerHTML = '';
        giftsMessageEl.classList.add(classes.hidden);
      }
    }
  }

  function init() {
    // Initialize tiered rewards bars
    const rewardsBars = document.querySelectorAll(selectors.tieredRewardsBar);
    rewardsBars.forEach(initTieredRewardsBar);

    // Initialize reward messages
    const rewardMessages = document.querySelectorAll(selectors.rewardMessages);
    rewardMessages.forEach(initRewardMessages);
  }

  function refresh() {
    // Capture current state before any updates so we can restore if DOM gets replaced
    const currentBars = document.querySelectorAll(selectors.tieredRewardsBar);
    currentBars.forEach(bar => {
      if (bar && bar.classList.contains(classes.loaded)) {
        lastState = captureState(bar);
      }
    });

    // Fetch fresh cart data and update
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartTotal = cart.total_price;

        // Update tiered rewards bars with fresh cart total
        const rewardsBars = document.querySelectorAll(selectors.tieredRewardsBar);
        rewardsBars.forEach(node => {
          if (node) {
            node.dataset.cartTotal = cartTotal;
            // Only remove success class to allow re-evaluation, keep loaded for visibility
            node.classList.remove(classes.success);
            updateTieredRewardsBarValues(node);
          }
        });

        // Update reward messages with fresh cart total
        const rewardMessages = document.querySelectorAll(selectors.rewardMessages);
        rewardMessages.forEach(node => {
          if (node) {
            node.dataset.cartTotal = cartTotal;
            initRewardMessages(node);
          }
        });
      })
      .catch(err => {
        console.error('Error fetching cart:', err);
        // Fallback to regular init
        init();
      });
  }

  // Update values without re-initializing (prevents glitch)
  function updateTieredRewardsBarValues(node) {
    if (!node) return;

    const dataset = node.dataset;
    const cartTotal = parseInt(dataset.cartTotal, 10) || 0;

    const shippingThreshold = parseThreshold(dataset.shippingThreshold);
    const giftboxThreshold = parseThreshold(dataset.giftboxThreshold);
    const earringThreshold = parseThreshold(dataset.earringThreshold);

    // Build thresholds array with custom labels from data attributes
    const shippingLabel = dataset.shippingLabel || 'Free shipping';
    const giftboxLabel = dataset.giftboxLabel || 'Free giftbox';
    const earringLabel = dataset.earringLabel || 'Free earring';
    const thresholds = [];
    if (shippingThreshold) thresholds.push({ type: 'shipping', value: shippingThreshold, label: shippingLabel });
    if (giftboxThreshold) thresholds.push({ type: 'giftbox', value: giftboxThreshold, label: giftboxLabel });
    if (earringThreshold) thresholds.push({ type: 'earring', value: earringThreshold, label: earringLabel });

    // Sort by threshold value
    thresholds.sort((a, b) => a.value - b.value);

    if (thresholds.length === 0) return;

    const maxThreshold = thresholds[thresholds.length - 1].value;

    // Determine currently reached tiers
    const currentlyReached = thresholds.filter(t => cartTotal >= t.value).map(t => t.type);

    // Detect newly achieved tiers
    const newlyAchieved = currentlyReached.filter(t => !previouslyReachedTiers.includes(t));

    // Update progress bar
    updateProgressBar(node, cartTotal, thresholds);

    // Show achievement message if a new tier was just unlocked
    if (newlyAchieved.length > 0) {
      const achievedTier = thresholds.find(t => t.type === newlyAchieved[newlyAchieved.length - 1]);
      if (achievedTier) {
        showAchievementMessage(node, achievedTier, cartTotal, thresholds);
      }
    } else if (!achievementActive) {
      // Update message normally (skip if achievement is currently displaying)
      updateMessage(node, cartTotal, thresholds);
    }

    // Store currently reached tiers for next comparison
    previouslyReachedTiers = currentlyReached;

    // Update tier markers (just the reached states, not positions)
    const tiers = node.querySelectorAll(selectors.tier);
    tiers.forEach(tierEl => {
      const tierType = tierEl.dataset.tier;
      const threshold = thresholds.find(t => t.type === tierType);
      if (threshold) {
        if (cartTotal >= threshold.value) {
          tierEl.classList.add(classes.tierReached);
        } else {
          tierEl.classList.remove(classes.tierReached);
        }
      }
    });

    // Check if all tiers are reached
    const allReached = cartTotal >= maxThreshold;

    // Update label states
    const labels = node.querySelectorAll(selectors.tierLabel);
    labels.forEach(labelEl => {
      const tierType = labelEl.dataset.tierLabel;
      const threshold = thresholds.find(t => t.type === tierType);
      if (threshold) {
        // Remove both classes first
        labelEl.classList.remove(classes.labelAchieved, classes.labelAllReached);

        if (cartTotal >= threshold.value) {
          if (allReached) {
            // All tiers reached - use green color
            labelEl.classList.add(classes.labelAllReached);
          } else {
            // This tier reached but not all - use black color
            labelEl.classList.add(classes.labelAchieved);
          }
        }
      }
    });

    // Check for all tiers complete
    if (cartTotal >= maxThreshold) {
      node.classList.add(classes.success);
    }
  }

  // Debounce function to prevent multiple rapid calls
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Debounced refresh to prevent glitches from multiple rapid updates
  const debouncedRefresh = debounce(refresh, 200);

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Listen for cart updates from theme
  document.addEventListener('cart:updated', debouncedRefresh);
  document.addEventListener('quick-cart:updated', debouncedRefresh);

  // Also listen for custom cart change events that themes may dispatch
  document.addEventListener('cart:change', debouncedRefresh);
  document.addEventListener('ajaxCart:updated', debouncedRefresh);

  // Re-initialize when quick cart content is refreshed via AJAX
  // Only observe the quick-cart container to avoid interfering with theme updates
  const observer = new MutationObserver(function(mutations) {
    let newBars = [];

    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList') {
        // Check for removed nodes first to capture the full visual state
        if (mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach(node => {
            if (node.nodeType === 1) {
              let removedBar = null;
              if (node.matches && node.matches(selectors.tieredRewardsBar)) {
                removedBar = node;
              } else if (node.querySelector) {
                removedBar = node.querySelector(selectors.tieredRewardsBar);
              }

              if (removedBar) {
                lastState = captureState(removedBar);
              }
            }
          });
        }

        // Check for added nodes
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              let addedBar = null;
              if (node.matches && node.matches(selectors.tieredRewardsBar)) {
                addedBar = node;
              } else if (node.querySelector) {
                addedBar = node.querySelector(selectors.tieredRewardsBar);
              }
              if (addedBar && !addedBar.classList.contains(classes.loaded)) {
                newBars.push(addedBar);
              }
            }
          });
        }
      }
    });

    // Immediately restore state on new bars (synchronous, no debounce)
    // This prevents any blank frame from being painted
    newBars.forEach(bar => {
      if (lastState) {
        restoreState(bar, lastState);
      }

      // Check if the new bar's cart total has unlocked a new tier
      // Do this synchronously so the correct achievement icon is shown before paint
      if (previouslyReachedInitialized && !achievementActive) {
        var dataset = bar.dataset;
        var newTotal = parseInt(dataset.cartTotal, 10) || 0;
        var st = parseThreshold(dataset.shippingThreshold);
        var gt = parseThreshold(dataset.giftboxThreshold);
        var et = parseThreshold(dataset.earringThreshold);
        var sl = dataset.shippingLabel || 'Free shipping';
        var gl = dataset.giftboxLabel || 'Free giftbox';
        var el2 = dataset.earringLabel || 'Free earring';
        var th = [];
        if (st) th.push({ type: 'shipping', value: st, label: sl });
        if (gt) th.push({ type: 'giftbox', value: gt, label: gl });
        if (et) th.push({ type: 'earring', value: et, label: el2 });
        th.sort(function(a, b) { return a.value - b.value; });
        var reached = th.filter(function(t) { return newTotal >= t.value; }).map(function(t) { return t.type; });
        var newlyAchieved = reached.filter(function(t) { return !previouslyReachedTiers.includes(t); });
        if (newlyAchieved.length > 0) {
          var achievedTier = th.find(function(t) { return t.type === newlyAchieved[newlyAchieved.length - 1]; });
          if (achievedTier) {
            showAchievementMessage(bar, achievedTier, newTotal, th);
            previouslyReachedTiers = reached;
          }
        }
      }
    });

    // Then schedule the animation to new values
    if (newBars.length > 0) {
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          newBars.forEach(bar => {
            bar.classList.remove(classes.noTransition);
            initTieredRewardsBar(bar);
          });

          const messages = document.querySelectorAll(selectors.rewardMessages);
          messages.forEach(msg => {
            if (msg) initRewardMessages(msg);
          });
        });
      });
    }
  });

  // Start observing - only observe quick-cart container if it exists
  function startObserving() {
    const quickCart = document.querySelector('.quick-cart');
    if (quickCart) {
      observer.observe(quickCart, { childList: true, subtree: true });
    } else {
      // Fallback: observe body but only for quick-cart additions
      observer.observe(document.body, { childList: true, subtree: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  // Expose refresh function globally for manual refresh if needed
  window.tieredRewardsBar = { refresh: refresh, init: init };

})();
